import {ApiLogObj2, StockItemUpdateFieldValue} from '../../../shared-utilities/models-old/datastructures';
import {Timestamp} from '@angular/fire/firestore';

/** Firestore `array-contains-any` supports at most 30 values. */
export const STOCK_HISTORY_MAX_SELECTED_ITEMS = 30;

const stockHistoryCodeCollator: Intl.Collator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

export interface IStockHistoryEvent extends ApiLogObj2 {
  fieldName: string;
  newValue: string;
  originalValue: string;
  stockCode: string;
}

export interface IStockHistoryPageResult {
  events: IStockHistoryEvent[];
  hasMore: boolean;
  lastDocumentId: string | null;
}

export interface IStockHistorySearchEvent {
  field: string;
  headerLabel: string;
  itemCodes: string[];
  selectedItems: IStockHistorySelectedItemSnapshot[];
}

/** Picker/Firestore snapshot used to resolve stock creation date in the history modal. */
export interface IStockHistorySelectedItemSnapshot {
  code: string;
  created?: unknown;
}

/** Creation metadata shown above the stock history grid. */
export interface IStockHistoryItemOrigin {
  code: string;
  createdByDisplayName: string;
  createdByUserId: string;
  createdDisplay: string;
}

/**
 * Formats a stock created timestamp for display in the history modal.
 */
export function formatStockHistoryCreatedDate(value: unknown): string {
  if (value === undefined || value === null || value === '') {
    return '—';
  }
  if (value instanceof Date) {
    return value.toLocaleDateString();
  }
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof value.toDate === 'function') {
    return value.toDate().toLocaleDateString();
  }
  if (typeof value === 'object' && value !== null && 'seconds' in value && typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000).toLocaleDateString();
  }
  if (typeof value === 'number') {
    return new Date(value).toLocaleDateString();
  }
  if (typeof value === 'string') {
    const parsed: Date = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
  }
  return String(value);
}

/**
 * Formats a single stock update value for display in the Original and New columns.
 */
export function formatStockHistoryValue(value: StockItemUpdateFieldValue | undefined): string {
  if (value === undefined || value === null || value === '') {
    return '—';
  }
  if (value instanceof Date) {
    return value.toLocaleDateString();
  }
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  return String(value);
}

/**
 * Sorts history rows by stock code, then newest event first within the same code.
 */
export function sortStockHistoryEventsByCode(events: IStockHistoryEvent[]): IStockHistoryEvent[] {
  return [...events].sort((left: IStockHistoryEvent, right: IStockHistoryEvent): number => {
    const byCode: number = stockHistoryCodeCollator.compare(left.stockCode ?? '', right.stockCode ?? '');
    if (byCode !== 0) {
      return byCode;
    }
    return getStockHistoryEventTime(right) - getStockHistoryEventTime(left);
  });
}

function getStockHistoryEventTime(event: IStockHistoryEvent): number {
  const date: unknown = event.executedDate ?? event.creationDate;
  if (!date) {
    return 0;
  }
  if (date instanceof Date) {
    return date.getTime();
  }
  if (isFirestoreTimestamp(date)) {
    return date.toMillis();
  }
  return 0;
}

function isFirestoreTimestamp(value: unknown): value is Timestamp {
  return typeof value === 'object' && value !== null && typeof (value as Timestamp).toMillis === 'function';
}
